import { createRoot } from 'react-dom/client'
import { init as initApm } from '@elastic/apm-rum'
import { FocusStyleManager } from '@blueprintjs/core/lib/esm/'

import * as Sentry from '@sentry/react'
import browserHistory from './utils/browser-history'
import { getState } from './utils/state'
import { router } from './utils/router'
import {
  APP_CONFIG_SENTRY_DSN,
  APP_CONFIG_APP_ENV,
  GIT_COMMIT,
  APP_CONFIG_APM_SERVICE_URL,
  APP_CONFIG_APM_FONTEND_SERVICE_NAME,
  APP_CONFIG_APM_CENTRAL_CONFIG,
} from '@config/app.config'

import { loadDefaultFonts } from '@client/utils/webfonts'
import { watchPageUnload, dropWhilePageUnloads } from '@client/utils/pageUnload'

import '../css/index.css'
import '../scss/app.scss'

loadDefaultFonts()

if (APP_CONFIG_APM_SERVICE_URL) {
  initApm({
    serviceName: APP_CONFIG_APM_FONTEND_SERVICE_NAME,
    serverUrl: APP_CONFIG_APM_SERVICE_URL,
    serviceVersion: GIT_COMMIT,
    environment: APP_CONFIG_APP_ENV,
    centralConfig: APP_CONFIG_APM_CENTRAL_CONFIG,
  })
}

watchPageUnload()

if (APP_CONFIG_SENTRY_DSN) {
  Sentry.init({
    dsn: APP_CONFIG_SENTRY_DSN,
    environment: APP_CONFIG_APP_ENV,
    release: GIT_COMMIT,
    ignoreErrors: [
      'Network request failed',
      'Failed to fetch',
      'Nätverksanslutningen förlorades.',
      'NetworkError when attempting to fetch resource.',
      'AbortError: The operation was aborted.',
    ],
    autoSessionTracking: false,
    beforeSend: (event, hint) => {
      // Requests cancelled because the user left the page (FE-1454).
      if (!dropWhilePageUnloads(event)) {
        return null
      }

      // filter out UnhandledRejection errors that have no information
      if (
        event !== undefined &&
        event.exception !== undefined &&
        event.exception.values !== undefined &&
        event.exception.values.length === 1
      ) {
        const e = event.exception.values[0]
        if (
          e.type === 'UnhandledRejection' &&
          (e.value === 'Non-Error promise rejection captured with value: ' ||
            `${e.value}`.startsWith(
              'Non-Error promise rejection captured with value: Object Not Found Matching Id:'
            ))
        ) {
          return null
        }
      }

      // Filter out NS_ERROR_FAILURE
      if (
        event !== undefined &&
        event.exception !== undefined &&
        event.exception.values !== undefined &&
        event.exception.values.length === 1
      ) {
        const e = event.exception.values[0]
        if (e.value === 'NS_ERROR_FAILURE') {
          return null
        }
      }

      return event
    },
  })
}

const context = {
  state: getState(),
}

const root = createRoot(document.getElementById('app'))

const render = (root) => async (location) => {
  const preparedLocation = location?.location ? location?.location : location

  const route = await router.resolve({ ...preparedLocation, ...context })

  if (route.redirect) {
    if (route.reload !== false) {
      window.location = route.redirect
      return
    } else {
      browserHistory.push(route.redirect)
    }
  }

  root.render(route.content)
}

const renderWithRoot = render(root)

// Listening for the history changes to the current location
browserHistory.listen(renderWithRoot)

// Initial Rendering for the initial location
renderWithRoot(browserHistory.location)

FocusStyleManager.onlyShowFocusOnTabs()
